import React from 'react';
import './ApiGuide.css';

interface ApiGuideProps {
  isOpen: boolean;
  onClose: () => void;
}

const ApiGuide: React.FC<ApiGuideProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="api-guide-overlay" onClick={onClose}>
      <div className="api-guide-modal" onClick={(e) => e.stopPropagation()}>
        <div className="api-guide-header">
          <h2>📚 Terrain Pro API Guide</h2>
          <button className="close-button" onClick={onClose}>✕</button>
        </div>
        
        <div className="api-guide-content">
          <section>
            <h3>🎯 Overview</h3>
            <p>
              The Terrain Pro API enables programmatic access to advanced terrain analysis 
              and solar project optimization. This RESTful API processes terrain files, 
              extracts elevation data, and performs complex calculations for solar installations.
            </p>
          </section>

          <section>
            <h3>🔐 Authentication</h3>
            <p>All API endpoints require JWT Bearer token authentication.</p>
            <div className="code-block">
              <code>Authorization: Bearer YOUR_JWT_TOKEN</code>
            </div>
            <ul>
              <li>Obtain your JWT token from your Terabase account</li>
              <li>Include the token in the Authorization header for all requests</li>
              <li>Tokens expire after a set period - refresh as needed</li>
            </ul>
          </section>

          <section>
            <h3>🔄 Workflow Steps</h3>
            
            <div className="workflow-step-guide">
              <h4>Step 1: Authenticate</h4>
              <p>Test your JWT token and verify authentication status.</p>
            </div>

            <div className="workflow-step-guide">
              <h4>Step 2: Validate Terrain File</h4>
              <p>
                Upload terrain data in supported formats (DXF, CSV, XLSX, PVC). 
                The API validates the file structure and creates project hierarchy.
              </p>
            </div>

            <div className="workflow-step-guide">
              <h4>Step 3: Extract Elevation Data</h4>
              <p>
                Process the validated terrain file to extract elevation points 
                and generate spatial data required for analysis.
              </p>
            </div>

            <div className="workflow-step-guide">
              <h4>Step 4: Run Analysis</h4>
              <p>
                Execute terrain analysis as a single run or batch run with parameter
                variations. The API processes configurations and generates optimized layout solutions.
              </p>
            </div>

            <div className="workflow-step-guide">
              <h4>Step 5: Download Results</h4>
              <p>
                Retrieve analysis outputs via presigned URLs. Results include 
                Excel reports, visualization images, and detailed data files.
              </p>
            </div>
          </section>

          <section>
            <h3>⚡ Best Practices</h3>
            <ul>
              <li>Always check job status before attempting to download results</li>
              <li>Use presigned URLs for file downloads (faster and more efficient)</li>
              <li>Implement retry logic for long-running operations</li>
              <li>Cache configuration data to minimize API calls</li>
              <li>Handle rate limits gracefully with exponential backoff</li>
            </ul>
          </section>

          <section>
            <h3>🚨 Error Handling</h3>
            <p>The API uses standard HTTP status codes:</p>
            <ul>
              <li><strong>200 OK</strong> - Request successful</li>
              <li><strong>201 Created</strong> - Resource created successfully</li>
              <li><strong>400 Bad Request</strong> - Invalid parameters</li>
              <li><strong>401 Unauthorized</strong> - Authentication failed</li>
              <li><strong>404 Not Found</strong> - Resource doesn't exist</li>
              <li><strong>500 Internal Server Error</strong> - Server-side error</li>
            </ul>
          </section>

          <section>
            <h3>📞 Support</h3>
            <p>
              For technical support, API access requests, or feature inquiries, 
              contact the Terabase development team.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ApiGuide;

